import { useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { scrollToSection } from "./useScrollToSection.js";
import { rendersHomeSections } from "../lib/navScope.js";
import useLocalizedHref from "./useLocalizedHref.js";

/**
 * Click handler for a link that points at a section of the home page.
 *
 * The site's home sections (`#features`, `#how-it-works`, `#pricing`) are only
 * in the DOM once React has rendered Home, so a native fragment link cannot
 * find them: nothing on this site handles `location.hash` on load. Every
 * in-app link to a home section therefore navigates first and scrolls after.
 *
 * The scroll itself — clearing the fixed navbar — is `scrollToSection`; this
 * hook only adds the "get to Home first" half.
 *
 * Navbar and Footer used to carry this same logic inline, written twice more.
 * They now call this hook: the "navigate first, then scroll" half became a real
 * mechanism rather than a timer, and three copies of a race is exactly the
 * shape of thing that only gets fixed in one of them.
 *
 * @param {string} targetId  id of the section on the home page
 * @returns {(e: MouseEvent) => void} onClick handler
 */
const useScrollToHomeSection = (targetId) => {
  const location = useLocation();
  const navigate = useNavigate();
  const localize = useLocalizedHref();

  return useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();

      const scrollTo = () => scrollToSection(targetId);

      if (rendersHomeSections(location.pathname)) {
        scrollTo();
      } else {
        // The intent travels with the navigation instead of racing it on a
        // timer; `useRouteScrollBehaviour` performs the scroll once Home has
        // mounted and revealed. See that hook for what the 100ms guess broke.
        // The home of the language being read, not always the English one:
        // reaching the plans from a Greek product page must not switch the
        // visitor's language on the way.
        navigate(localize("/"), { state: { scrollToId: targetId } });
      }
    },
    [localize, location.pathname, navigate, targetId],
  );
};

export default useScrollToHomeSection;
